import {Validators} from '@angular/forms';

import {AccountInfoInputModel, CryptoWalletTypeEnum, CurrencyTypesEnum, LinkedAccountStatusesEnum} from '@spa/api';

import {
    IbanValidator,
    SwiftValidator,
    InArrayValidator,
    AddressValidator,
    SortCodeValidator,
    WhitespaceValidator,
    LatinDigitsValidator
} from '@spa/validators';

import {ComponentTypesEnum} from '../../app/shared/enums/common';

import {Helper} from '../../app/shared/classes';
import {BaseControl, CountryAutocompleteControl, SelectControl} from '../../app/shared/classes/controls';

import {BankAccountTypesEnum} from '../../app/entries/panel/enums';

export const BankAccountsFormAccountTypeSelector = 'accountType';
export const BankAccountsFormIbanSelector = 'iban';
export const BankAccountsFormAccountNumberSelector = 'accountNumber';
export const BankAccountsFormSortCodeSelector = 'sortCode';
export const BankAccountsFormBankNameSelector = 'bankName';
export const BankAccountsFormBicSelector = 'bic';
export const BankAccountsFormBankCountrySelector = 'bankCountry';
export const BankAccountsFormBankAddressSelector = 'bankAddress';
export const BankAccountsFormStatusSelector = 'status';
export const BankAccountsFormCurrencySelector = 'currency';
export const CryptoWalletAddressSelector = 'crypto_wallet_address';
export const CryptoWalletNameSelector = 'crypto_wallet_name';
export const CryptoNetworkCodeSelector = 'crypto_network_code';
export const CryptoWalletCodeSelector = 'crypto_code';
export const CryptoWalletTypeSelector = 'crypto_wallet_type';

export interface BankAccountsFormControlsInterface {
    accountType: SelectControl;
    iban: BaseControl;
    accountNumber: BaseControl;
    bankName: BaseControl;
    sortCode: BaseControl;
    bic: BaseControl;
    currency: SelectControl;
    beneficiaryBankCountry: CountryAutocompleteControl;
    beneficiaryBankAddress: BaseControl;
    status: SelectControl;
    crypto_wallet_address: BaseControl;
    crypto_wallet_name: BaseControl;
    crypto_network_code: SelectControl;
    crypto_code: SelectControl;
    crypto_wallet_type: SelectControl;
}

export const BankAccountsFormControls = (
    floatingLabelNever: boolean,
    statusNewDisabled = false
): BankAccountsFormControlsInterface => {
    return {
        accountType: new SelectControl({
            name: BankAccountsFormAccountTypeSelector,
            value: null,
            placeholder: 'Account Type',
            validators: [
                Validators.required,
                InArrayValidator([
                    BankAccountTypesEnum.Iban,
                    BankAccountTypesEnum.BankAccount,
                    BankAccountTypesEnum.SortCode,
                    BankAccountTypesEnum.CryptoWallet
                ])
            ],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getBankAccountTypeName(BankAccountTypesEnum.Iban),
                    value: BankAccountTypesEnum.Iban
                },
                {
                    label: Helper.textsHelper().getBankAccountTypeName(BankAccountTypesEnum.BankAccount),
                    value: BankAccountTypesEnum.BankAccount
                },
                {
                    label: Helper.textsHelper().getBankAccountTypeName(BankAccountTypesEnum.SortCode),
                    value: BankAccountTypesEnum.SortCode
                },
                {
                    label: Helper.textsHelper().getBankAccountTypeName(BankAccountTypesEnum.CryptoWallet),
                    value: BankAccountTypesEnum.CryptoWallet
                },
            ],
            floatingLabelNever
        }),
        iban: new BaseControl({
            name: BankAccountsFormIbanSelector,
            value: '',
            placeholder: 'IBAN',
            validators: [WhitespaceValidator(), IbanValidator()],
            componentType: ComponentTypesEnum.Text,
            nonCapitalizedPlaceholder: true,
            floatingLabelNever,
            upperCaseValue: true
        }),
        accountNumber: new BaseControl({
            name: BankAccountsFormAccountNumberSelector,
            value: '',
            placeholder: 'Account Number',
            validators: [WhitespaceValidator(), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        bankName: new BaseControl({
            name: BankAccountsFormBankNameSelector,
            value: '',
            placeholder: 'Bank Name',
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        sortCode: new BaseControl({
            name: BankAccountsFormSortCodeSelector,
            value: '',
            placeholder: 'Sort Code(UK only)',
            validators: [WhitespaceValidator(), SortCodeValidator()],
            componentType: ComponentTypesEnum.Text,
            nonCapitalizedPlaceholder: true,
            floatingLabelNever
        }),
        bic: new BaseControl({
            name: BankAccountsFormBicSelector,
            value: '',
            placeholder: 'Bic/Swift/Routing No',
            validators: [WhitespaceValidator(), LatinDigitsValidator(), SwiftValidator()],
            componentType: ComponentTypesEnum.Text,
            nonCapitalizedPlaceholder: true,
            floatingLabelNever,
            upperCaseValue: true
        }),
        currency: new SelectControl({
            name: BankAccountsFormCurrencySelector,
            value: null,
            placeholder: 'Currency',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: 'EUR',
                    value: 'EUR',
                },
                {
                    label: 'BGN',
                    value: 'BGN'
                },
                {
                    label: 'USD',
                    value: 'USD'
                },
                {
                    label: 'GBP',
                    value: 'GBP'
                }
            ],
            floatingLabelNever
        }),
        beneficiaryBankCountry: new CountryAutocompleteControl({
            name: BankAccountsFormBankCountrySelector,
            value: '',
            placeholder: 'Beneficiary Bank\'s Country',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.CountryAutocomplete,
            countries: [],
            options: [],
            floatingLabelNever,
        }),
        beneficiaryBankAddress: new BaseControl({
            name: BankAccountsFormBankAddressSelector,
            value: '',
            placeholder: 'Beneficiary Bank\'s Address',
            validators: [AddressValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        status: new SelectControl(
            {
                name: BankAccountsFormStatusSelector,
                value: null,
                placeholder: 'Status',
                validators: [
                    Validators.required,
                    InArrayValidator([
                        LinkedAccountStatusesEnum.New,
                        LinkedAccountStatusesEnum.Active,
                        LinkedAccountStatusesEnum.Inactive
                    ])
                ],
                componentType: ComponentTypesEnum.Select,
                options: [
                    {
                        label: Helper.textsHelper().getBankAccountStatusName(LinkedAccountStatusesEnum.New),
                        value: LinkedAccountStatusesEnum.New,
                        disabled: statusNewDisabled
                    },
                    {
                        label: Helper.textsHelper().getBankAccountStatusName(LinkedAccountStatusesEnum.Active),
                        value: LinkedAccountStatusesEnum.Active
                    },
                    {
                        label: Helper.textsHelper().getBankAccountStatusName(LinkedAccountStatusesEnum.Inactive),
                        value: LinkedAccountStatusesEnum.Inactive
                    },
                ],
                floatingLabelNever
            }
        ),
        crypto_wallet_name: new BaseControl({
            name: CryptoWalletNameSelector,
            value: '',
            placeholder: 'Wallet Name',
            validators: [],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        crypto_wallet_address: new BaseControl({
            name: CryptoWalletAddressSelector,
            value: '',
            placeholder: 'Address',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        crypto_network_code: new SelectControl(
            {
                name: CryptoNetworkCodeSelector,
                value: null,
                placeholder: 'Network',
                validators: [Validators.required],
                componentType: ComponentTypesEnum.Select,
                options: [],
                floatingLabelNever
            }
        ),
        crypto_code: new SelectControl(
            {
                name: CryptoWalletCodeSelector,
                value: null,
                placeholder: 'Currency',
                validators: [Validators.required],
                componentType: ComponentTypesEnum.Select,
                options: [],
                floatingLabelNever
            }
        ),
        crypto_wallet_type: new SelectControl(
            {
                name: CryptoWalletTypeSelector,
                value: null,
                placeholder: 'Wallet Type',
                validators: [ InArrayValidator([
                    CryptoWalletTypeEnum.Custodial,
                    CryptoWalletTypeEnum.NonCustodial,
                    CryptoWalletTypeEnum.WatchOnly
                ])],
                componentType: ComponentTypesEnum.Select,
                options: [
                    {
                        label: Helper.textsHelper().getCryptoWalletTypeName(CryptoWalletTypeEnum.Custodial),
                        value: CryptoWalletTypeEnum.Custodial
                    },
                    {
                        label: Helper.textsHelper().getCryptoWalletTypeName(CryptoWalletTypeEnum.NonCustodial),
                        value: CryptoWalletTypeEnum.NonCustodial
                    },
                    {
                        label: Helper.textsHelper().getCryptoWalletTypeName(CryptoWalletTypeEnum.WatchOnly),
                        value: CryptoWalletTypeEnum.WatchOnly
                    },
                ],
                floatingLabelNever
            }
        ),
    };
};
